import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import {
  BoxInClinicListRequestParams,
  CreateTransmissionReportRequest,
  CreateTransmissionReportResponse,
  GetUnassignedFilesResponse,
  SearchPatientsRequest,
  SearchPatientsResponse
} from '@/types/boxInClinic';

export const boxInClinicApiSlice = createApi({
  reducerPath: 'boxInClinicApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.BoxInClinic],
  endpoints: (builder) => ({
    getAllBoxInClinic: builder.query<GetUnassignedFilesResponse, BoxInClinicListRequestParams>({
      query: (params) => ({
        url: API_ENDPOINTS.BOX_IN_CLINIC.LIST,
        method: 'POST',
        body: params
      }),
      providesTags: (_result, _error, _params) => [{ type: API_SLICE.BoxInClinic, id: 'LIST' }]
    }),
    getBoxFileDownloadUrl: builder.query<{ downloadUrl: string }, string>({
      query: (fileId) => ({
        url: API_ENDPOINTS.BOX_IN_CLINIC.DOWNLOAD_FILE(fileId)
      })
    }),
    searchPatients: builder.mutation<SearchPatientsResponse, SearchPatientsRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.BOX_IN_CLINIC.SEARCH_PATIENTS,
        method: 'POST',
        body
      })
    }),
    createTransmissionReport: builder.mutation<
      CreateTransmissionReportResponse,
      CreateTransmissionReportRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOX_IN_CLINIC.CREATE_REPORT,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: API_SLICE.BoxInClinic, id: 'LIST' }]
    }),
    deleteBoxFile: builder.mutation<{ success: boolean; message?: string }, string>({
      query: (fileId) => ({
        url: API_ENDPOINTS.BOX_IN_CLINIC.DELETE_FILE(fileId),
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: API_SLICE.BoxInClinic, id: 'LIST' }]
    })
  })
});

export const {
  useGetAllBoxInClinicQuery,
  useSearchPatientsMutation,
  useGetBoxFileDownloadUrlQuery,
  useCreateTransmissionReportMutation,
  useLazyGetBoxFileDownloadUrlQuery,
  useDeleteBoxFileMutation
} = boxInClinicApiSlice;
